<script setup lang="ts">
const { $containment } = useNuxtApp();

const { fetchUserProfile } = useUserData();
const { isLoading } = storeToRefs(useUserDataStore());
const { modalType } = storeToRefs(useLayoutStore());
const { closeModal } = useLayoutStore();
const { currentRouteName } = useRoutes();

const currentPendencyName = computed(() => {
  return $containment.currentPendency;
});

const showDependencyFooter = computed(() => {
  return (
    (currentPendencyName.value === 'email' ||
      currentPendencyName.value === 'phone') &&
    // inactive for now
    false
  );
});

const onSkipVerificationButtonClick = () => {
  if (!currentPendencyName.value) return;

  $containment.solve({
    name: currentPendencyName.value,
    customValidator: () => true,
  });
};

watch(
  currentPendencyName,
  (pendencyName) => {
    if (modalType.value === 'verification' && !pendencyName) {
      closeModal();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchUserProfile();
});
</script>

<template>
  <div
    :class="$style.modalContent"
    :data-name="currentPendencyName"
    data-type="pendency"
  >
    <div :class="$style.modalContentInner">
      <BaseLoading
        class="!bg-bg-primary !absolute"
        :icon-loader="true"
        :show="isLoading"
      />

      <AuthVerificationMessageSteps />

      <AuthVerificationPassword v-if="currentPendencyName === 'password'" />

      <AuthVerificationDocs v-else-if="currentPendencyName === 'docs'" />

      <AuthVerificationAddress v-else-if="currentPendencyName === 'address'" />

      <AuthVerificationEmail v-else-if="currentPendencyName === 'email'" />

      <AuthVerificationPhoneCheck v-else-if="currentPendencyName === 'phone'" />

      <AuthVerificationGps v-else-if="currentPendencyName === 'gps'" />

      <AuthVerificationKyc v-else-if="currentPendencyName === 'kyc'" />

      <AuthVerificationLimits
        v-else-if="currentPendencyName === 'user_limits'"
      />

      <AuthVerificationBankAccount
        v-else-if="currentPendencyName === 'bank_account'"
      />

      <AuthVerificationContact v-else-if="currentPendencyName === 'contact'" />

      <AuthVerificationLoading v-else-if="isLoading" />

      <AuthVerificationEmpty v-else-if="currentRouteName === 'pending'" />
    </div>

    <div
      v-if="showDependencyFooter"
      :class="$style.dependencyFooter"
    >
      <button
        :class="$style.backButton"
        @click="onSkipVerificationButtonClick"
      >
        <template v-if="currentPendencyName === 'email'">
          <NuxtIcon name="arrows/angle_left" />
          {{ $t('verification.email.back') }}
        </template>
        <template v-else-if="currentPendencyName === 'phone'">
          <NuxtIcon name="arrows/angle_left" />
          {{ $t('verification.sms.back') }}
        </template>
      </button>
    </div>
  </div>
</template>

<style module lang="scss">
.modalContent {
  @apply flex-1 flex flex-col justify-between h-full max-h-[calc(100vh-150px)] overflow-y-auto relative p-3;

  .modalContentInner {
    @apply flex flex-col justify-center flex-1 text-texts text-center;
  }

  .dependencyFooter {
    @apply h-12 min-w-full sticky bottom-0 bg-black/20 z-20 -m-7 mt-5;

    .backButton {
      @apply m-auto h-full flex justify-center items-center gap-2 text-texts/40 font-bold text-sm;
    }
  }
}
</style>
